import { FC } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { Button } from '@new/common/ui/Button/Button';

import { TRANSCRIPTS_ROUTE } from '../../lib/routes';
import { PlanStepCard } from '../PlanStepCard/PlanStepCard';

const INTRO_VIDEO_URL = 'https://www.youtube.com/embed/mONwcZL0N4g';
const INTRO_VIDEO_DURATION = '11:12';

interface BuildPlanIntroStepProps {
  onChooseDegree: () => void;
}

export const BuildPlanIntroStep: FC<BuildPlanIntroStepProps> = ({ onChooseDegree }) => {
  const navigate = useNavigate();
  const onAddTranscripts = () => navigate({ to: TRANSCRIPTS_ROUTE });

  return (
    <PlanStepCard>
      <div className="flex flex-col gap-3">
        <h2 className="m-0! [font-family:var(--font-arvo)]! [font-size:var(--fs-h4)]! font-bold text-(--neutral-black-NB900)!">
          Your degree plan, made for you
        </h2>
        <p className="m-0! [font-size:var(--fs-p3)]! text-(--neutral-black-NB700)! leading-tight">
          We'll use your transcript evaluation and awarded credits to build a personalized plan
          for your degree. You can review and adjust it before confirming.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-(--neutral-black-NB200)">
        <div className="relative aspect-video w-full">
          <iframe
            title="Onboarding video"
            src={INTRO_VIDEO_URL}
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
          <span className="absolute right-3 top-3 rounded-md bg-(--neutral-black-NB1100)/70 px-2 py-1 [font-size:var(--fs-l5)]! font-semibold text-(--neutral-white-NW1000)!">
            {INTRO_VIDEO_DURATION}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 bg-(--neutral-white-NW1000) p-4">
          <span className="[font-size:var(--fs-b4)]! font-semibold text-(--neutral-black-NB900)!">
            See how your degree plan works
          </span>
          <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
            A quick introduction
          </span>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button
          variant="primary"
          className="w-full"
          onClick={onChooseDegree}
          text="Choose your degree"
        />
        <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)! leading-tight m-0!">
          <button
            type="button"
            onClick={onAddTranscripts}
            className="cursor-pointer text-(--primary-O900)! font-semibold underline-offset-2 hover:underline"
          >
            Have more transcripts?
          </button>{' '}
          Upload them so your plan can include eligible transfer credits.
        </p>
      </div>
    </PlanStepCard>
  );
};
